import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18nInstance from '@/i18n/config';
import { useUIStore } from '@/stores';
import {
  LogIn,
  LogOut,
  ChevronDown,
  ChevronUp,
  Key,
  Copy,
  Check,
  RefreshCw,
  Download,
} from 'lucide-react';
import { apiGet, apiPost } from '@/lib/api';
import { navigateTo } from '@/lib/hashRouter';
import { useResponsiveLayout } from '@/hooks/ui/useResponsiveLayout';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
// import LanguageSwitcher from '@/components/LanguageSwitcher';

interface TokenInfo {
  id: number;
  token: string;
  expiredAt: string;
}

interface TokenResponse {
  code: number;
  message: string;
  tokens: TokenInfo[];
  baseUrl: string;
}

interface UserSectionProps {
  isLoggedIn: boolean;
  userInfo?: { name?: string; email?: string };
  onLogin: () => void;
  onLogout: () => void;
}
const UserSection = React.memo(
  function UserSection({
    isLoggedIn,
    userInfo,
    onLogin,
    onLogout,
  }: UserSectionProps) {
    const { t } = useTranslation();
    const { setIsSidebarCollapsed } = useUIStore();
    const { isMobile } = useResponsiveLayout();
    const [userDropdownOpen, setUserDropdownOpen] = useState(false);
    const [showTokenModal, setShowTokenModal] = useState(false);
    const [tokenData, setTokenData] = useState<{
      baseUrl: string;
      tokens: TokenInfo[];
    } | null>(null);
    const [isLoadingTokens, setIsLoadingTokens] = useState(false);
    const [copiedField, setCopiedField] = useState<string | null>(null);
    const [isDownloading, setIsDownloading] = useState<string | null>(null);
    const [, setForceUpdate] = useState(0);

    // 监听语言变化 - 使用我们配置的 i18n 实例
    useEffect(() => {
      const handleLanguageChange = () => {
        setForceUpdate(n => n + 1);
      };
      i18nInstance.on('languageChanged', handleLanguageChange);
      return () => {
        i18nInstance.off('languageChanged', handleLanguageChange);
      };
    }, []);

    // Fetch API tokens
    const fetchTokens = useCallback(async () => {
      setIsLoadingTokens(true);
      try {
        const response = await apiGet<TokenResponse>('/api/ai/cc/tokens');
        if (response && response.code === 200) {
          setTokenData({
            baseUrl: response.baseUrl || '',
            tokens: response.tokens || [],
          });
        }
      } catch (error) {
        console.error('Failed to fetch tokens:', error);
      } finally {
        setIsLoadingTokens(false);
      }
    }, []);

    // Refresh all tokens
    const refreshAllTokens = useCallback(async () => {
      if (!tokenData?.tokens?.length) return;
      setIsLoadingTokens(true);
      try {
        // Refresh all tokens in parallel
        await Promise.all(
          tokenData.tokens.map(token =>
            apiPost(`/api/ai/cc/token/${token.id}/refresh`, {})
          )
        );
        // Re-fetch tokens to get updated expiredAt
        await fetchTokens();
      } catch (error) {
        console.error('Failed to refresh all tokens:', error);
      } finally {
        setIsLoadingTokens(false);
      }
    }, [tokenData?.tokens, fetchTokens]);

    // Copy to clipboard
    const copyToClipboard = useCallback(
      async (text: string, fieldId: string) => {
        try {
          await navigator.clipboard.writeText(text);
          setCopiedField(fieldId);
          setTimeout(() => setCopiedField(null), 2000);
        } catch (error) {
          console.error('Failed to copy:', error);
        }
      },
      []
    );

    // Open token modal (kept for backward compatibility, currently not used)
    // const handleOpenTokenModal = useCallback(() => {
    //   setShowTokenModal(true);
    //   setUserDropdownOpen(false);
    //   fetchTokens();
    // }, [fetchTokens]);

    // Open user settings
    const handleOpenUserSettings = useCallback(() => {
      console.log('Opening user settings...'); // Debug log
      navigateTo({ type: 'user-settings' });
      setUserDropdownOpen(false);
      // 🔥 修复：只在移动端自动关闭侧边栏，PC 端保持不变
      if (isMobile) {
        setIsSidebarCollapsed(true);
      }
    }, [setIsSidebarCollapsed, isMobile]);

    // Download settings file
    const downloadSettings = useCallback(
      async (tokenId: number, type: 'cc' | 'pi') => {
        setIsDownloading(`${tokenId}-${type}`);
        try {
          const endpoint =
            type === 'cc'
              ? `/api/ai/cc/tokens/${tokenId}/cc_settings/download`
              : `/api/ai/cc/tokens/${tokenId}/pi_settings/download`;

          const token = localStorage.getItem('user-token');
          const response = await fetch(endpoint, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = type === 'cc' ? 'settings.json' : 'models.json';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();
          }
        } catch (error) {
          console.error(`Failed to download ${type} settings:`, error);
        } finally {
          setIsDownloading(null);
        }
      },
      []
    );

    const getUserInitials = () => {
      const name = userInfo?.name || userInfo?.email || 'User';
      return name.substring(0, 2).toUpperCase();
    };
    if (!isLoggedIn) {
      return (
        <div className="p-4">
          <button
            onClick={onLogin}
            className="w-full flex items-center justify-center gap-2 py-2 text-gray-800 hover:text-blue-700 bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-200 rounded-lg transition-all duration-200 text-sm font-medium shadow-sm hover:shadow"
          >
            <LogIn size={16} />
            <span>{t('sidebar.login')}</span>
          </button>
        </div>
      );
    }
    return (
      <div className="relative">
        {/* User info button */}
        <button
          onClick={() => setUserDropdownOpen(!userDropdownOpen)}
          className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-50 transition-colors"
        >
          <div className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-white text-sm font-bold">
              {getUserInitials()}
            </span>
          </div>
          <div className="flex-1 min-w-0 text-left">
            <div className="text-sm font-medium text-gray-800 truncate">
              {userInfo?.name || userInfo?.email || 'User'}
            </div>
          </div>
          {userDropdownOpen ? (
            <ChevronUp size={16} className="text-gray-400" />
          ) : (
            <ChevronDown size={16} className="text-gray-400" />
          )}
        </button>
        {/* Dropdown menu */}
        {userDropdownOpen && (
          <div className="absolute bottom-full left-0 right-0 bg-white border border-gray-200 rounded-t-lg shadow-lg">
            <button
              onClick={handleOpenUserSettings}
              className="w-full flex items-center gap-3 px-4 py-3 text-gray-600 hover:bg-gray-50 transition-colors text-sm border-b border-gray-100"
            >
              <Key size={16} />
              <span>{t('sidebar.userSettings')}</span>
            </button>
            <button
              onClick={() => {
                onLogout();
                setUserDropdownOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 text-gray-600 hover:bg-gray-50 transition-colors text-sm"
            >
              <LogOut size={16} />
              <span>{t('sidebar.logout')}</span>
            </button>
          </div>
        )}

        {/* API Token Modal (kept for backward compatibility) */}
        <Dialog open={showTokenModal} onOpenChange={setShowTokenModal}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('sidebar.apiToken')}</DialogTitle>
            </DialogHeader>

            <div className="px-6 py-4 space-y-4">
              {isLoadingTokens ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span className="ml-2 text-gray-500">
                    {t('common.loading')}
                  </span>
                </div>
              ) : tokenData ? (
                <>
                  {/* Base URL */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('sidebar.baseUrl')}
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={tokenData.baseUrl}
                        readOnly
                        className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 font-mono"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          copyToClipboard(tokenData.baseUrl, 'baseUrl')
                        }
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title={t('sidebar.copyBaseUrl')}
                      >
                        {copiedField === 'baseUrl' ? (
                          <Check size={16} className="text-green-500" />
                        ) : (
                          <Copy size={16} className="text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Tokens */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('sidebar.tokens')}
                    </label>
                    <div className="space-y-3">
                      {tokenData.tokens.map((token, index) => (
                        <div
                          key={token.id}
                          className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <input
                              type="text"
                              value={token.token}
                              readOnly
                              className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 font-mono"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                copyToClipboard(token.token, `token-${index}`)
                              }
                              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                              title={t('sidebar.copyToken')}
                            >
                              {copiedField === `token-${index}` ? (
                                <Check size={16} className="text-green-500" />
                              ) : (
                                <Copy size={16} className="text-gray-500" />
                              )}
                            </button>
                          </div>
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center justify-between text-xs">
                              <span
                                className={
                                  Date.parse(token.expiredAt) < Date.now()
                                    ? 'text-red-500 font-medium'
                                    : 'text-gray-500'
                                }
                              >
                                {t('sidebar.expires')}:{' '}
                                {new Date(token.expiredAt).toLocaleString()}
                              </span>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    downloadSettings(token.id, 'cc')
                                  }
                                  disabled={isDownloading === `${token.id}-cc`}
                                  className="flex items-center gap-1 px-2 py-1 text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                                >
                                  <Download
                                    size={12}
                                    className={
                                      isDownloading === `${token.id}-cc`
                                        ? 'animate-bounce'
                                        : ''
                                    }
                                  />
                                  <span>{t('sidebar.ccSetting')}</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    downloadSettings(token.id, 'pi')
                                  }
                                  disabled={isDownloading === `${token.id}-pi`}
                                  className="flex items-center gap-1 px-2 py-1 text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                                >
                                  <Download
                                    size={12}
                                    className={
                                      isDownloading === `${token.id}-pi`
                                        ? 'animate-bounce'
                                        : ''
                                    }
                                  />
                                  <span>{t('sidebar.piSetting')}</span>
                                </button>
                              </div>
                            </div>
                            {Date.parse(token.expiredAt) < Date.now() && (
                              <span className="text-xs text-red-500">
                                {t('sidebar.tokenExpired')}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                      {tokenData.tokens.length === 0 && (
                        <div className="text-center py-4 text-gray-500 text-sm">
                          {t('sidebar.noTokens')}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {t('sidebar.failedToLoadTokens')}
                </div>
              )}
            </div>

            {/* Installation Instructions */}
            <div className="mx-6 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-gray-700">
              <p className="font-medium mb-2 text-blue-800">
                {t('sidebar.instructions')}:
              </p>
              <ul className="space-y-2">
                <li>
                  <strong>{t('sidebar.claudeCodeSetting')}:</strong>
                  <code className="text-xs bg-white px-2 py-1 rounded border border-gray-200 ml-2">
                    C:\Users\your_account\.claude\settings.json
                  </code>
                </li>
                <li>
                  <strong>{t('sidebar.piMonoSetting')}:</strong>
                  <code className="text-xs bg-white px-2 py-1 rounded border border-gray-200 ml-2">
                    C:\Users\your_account\.pi\agent\models.json
                  </code>
                </li>
              </ul>
            </div>

            <DialogFooter className="flex justify-center sm:justify-center">
              <Button
                type="button"
                onClick={refreshAllTokens}
                disabled={isLoadingTokens || !tokenData?.tokens?.length}
                className="flex items-center gap-2"
              >
                <RefreshCw
                  size={16}
                  className={isLoadingTokens ? 'animate-spin' : ''}
                />
                <span>{t('sidebar.refresh')}</span>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // 自定义比较函数：忽略函数引用的变化
    return (
      prevProps.isLoggedIn === nextProps.isLoggedIn &&
      prevProps.userInfo?.name === nextProps.userInfo?.name &&
      prevProps.userInfo?.email === nextProps.userInfo?.email
      // 故意忽略 onLogin 和 onLogout 的比较，因为它们的功能不会变
    );
  }
);

export default UserSection;
